// src/pages/Cart.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useCart } from '../contexts/CartContext';
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  Alert,
  Snackbar
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabaseClient';
import CartTable from '../components/Cart/CartTable';
import CartSummary from '../components/Cart/CartSummary';

const Cart = () => {
  const { cartItems, cartCount, removeFromCart, updateQuantity, clearCart, setPaymentMethod, paymentMethod } = useCart();
  const navigate = useNavigate();
  const [stockLimits, setStockLimits] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshingStock, setRefreshingStock] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  
  // Shipping related state
  const [shippingPrices, setShippingPrices] = useState([]);
  const [selectedGovernorate, setSelectedGovernorate] = useState('');
  const [shippingPrice, setShippingPrice] = useState(0);
  const [loadingShipping, setLoadingShipping] = useState(true);
  const [productShippingInfo, setProductShippingInfo] = useState({});

  const totalAmount = cartItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  // Check if all items have free shipping
  const hasAllFreeShipping = useMemo(() => {
    if (cartItems.length === 0) return false;
    return cartItems.every(item => productShippingInfo[item.id]?.free_shipping === true);
  }, [cartItems, productShippingInfo]);

  // Calculate final shipping price (0 if all items have free shipping)
  const finalShippingPrice = hasAllFreeShipping ? 0 : shippingPrice;

  // Calculate final total including shipping
  const finalTotal = totalAmount + finalShippingPrice;

  // Memoize cart items serialization to avoid complex expressions in dependency arrays
  const cartItemsHash = useMemo(() => JSON.stringify(cartItems), [cartItems]);
  console.log('Cart items hash:', cartItemsHash);

  // Fetch product shipping information - moved to useCallback
  const fetchProductShippingInfo = useCallback(async () => {
    if (cartItems.length === 0) return;
    
    try {
      const productIds = [...new Set(cartItems.map(item => item.id))];
      const { data, error } = await supabase
        .from('products')
        .select('id, free_shipping')
        .in('id', productIds);
      
      if (error) {
        console.error('Error fetching product shipping info:', error);
        return;
      }
      
      const shippingInfoMap = {};
      data.forEach(product => {
        shippingInfoMap[product.id] = { free_shipping: product.free_shipping };
      });
      
      setProductShippingInfo(shippingInfoMap);
    } catch (err) {
      console.error('Error fetching product shipping info:', err);
    }
  }, [cartItems]);

  // Fetch shipping prices
  const fetchShippingPrices = async () => {
    try {
      const { data, error } = await supabase
        .from('shipping_prices')
        .select('*')
        .order('governorate_name_ar');
      
      if (error) {
        console.error('Error fetching shipping prices:', error);
        setNotification({
          open: true,
          message: 'خطأ في تحميل أسعار الشحن',
          severity: 'error'
        });
        return;
      }
      
      setShippingPrices(data || []);
    } catch (err) {
      console.error('Error fetching shipping prices:', err);
    } finally {
      setLoadingShipping(false);
    }
  };

  // Handle governorate selection
  const handleGovernorateChange = (event) => {
    const governorateId = event.target.value;
    setSelectedGovernorate(governorateId);
    
    if (governorateId) {
      const selectedPrice = shippingPrices.find(price => price.id === governorateId);
      setShippingPrice(selectedPrice ? selectedPrice.shipping_price : 0);
    } else {
      setShippingPrice(0);
    }
  };

  // Function to update stock data without changing the main loading state
  const updateStockData = useCallback(async () => {
    try {
      // Create a copy to avoid issues if items are modified during the operation
      const currentItems = [...cartItems];
      
      if (currentItems.length === 0) {
        return;
      }
      
      const promises = currentItems.map(async (item) => {
        try {
          const { data, error } = await supabase
            .from('product_color_sizes')
            .select('stock')
            .eq('product_color_id', item.colorId)
            .eq('size', item.size)
            .single();
            
          if (error) {
            console.error('Supabase error:', error);
            return { key: `${item.id}-${item.colorId}-${item.size}`, stock: 0 };
          }
          
          return { key: `${item.id}-${item.colorId}-${item.size}`, stock: data?.stock || 0 };
        } catch (err) {
          console.error('Error fetching stock for item:', err);
          return { key: `${item.id}-${item.colorId}-${item.size}`, stock: 0 };
        }
      });
      
      const results = await Promise.all(promises);
      const map = {};
      results.forEach(({ key, stock }) => { map[key] = stock; });
      setStockLimits(map);
      
      // adjust quantities exceeding stock but keep items in cart even if stock is 0
      currentItems.forEach(item => {
        const key = `${item.id}-${item.colorId}-${item.size}`;
        const limit = map[key] || 0;
        if (item.quantity > limit && limit > 0) {
          // Only adjust quantity if stock is greater than 0
          updateQuantity(item.id, item.colorId, item.size, limit);
        } else if (limit === 0 && item.quantity > 0) {
          // If stock is 0, keep the item in cart but set a minimal quantity (1)
          // This maintains the item in cart while indicating it's out of stock
          updateQuantity(item.id, item.colorId, item.size, 1);
        }
      });
    } catch (err) {
      console.error('Error in updateStockData:', err);
    }
  }, [cartItems, updateQuantity]);

  // Function to fetch stock data with initial loading
  const fetchInitialStock = useCallback(async () => {
    if (cartItems.length === 0) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      await updateStockData();
      await fetchProductShippingInfo();
    } catch (err) {
      console.error('Error fetching initial stock:', err);
    } finally {
      // Always set loading to false, even if there was an error
      setLoading(false);
    }
  }, [cartItems.length, updateStockData, fetchProductShippingInfo]);
  
  // Fetch stock limits for all items in cart on component mount and when cart changes
  useEffect(() => {
    fetchInitialStock();
  }, [fetchInitialStock]);

  // Fetch shipping prices on component mount
  useEffect(() => {
    fetchShippingPrices();
  }, []);

  // Fetch product shipping info when cart items change
  useEffect(() => {
    if (cartItems.length > 0) {
      fetchProductShippingInfo();
    } else {
      setProductShippingInfo({});
    }
  }, [cartItems, fetchProductShippingInfo]);

  // Handler for when dropdown is opened - background refresh
  const handleDropdownOpen = async () => {
    if (refreshingStock) return; // Prevent multiple simultaneous refreshes
    
    setRefreshingStock(true);
    try {
      await updateStockData();
    } catch (err) {
      console.error('Error refreshing stock:', err);
    } finally {
      setRefreshingStock(false);
    }
  };
  
  // Checkout eligibility check - returns products that aren't available
  const getUnavailableProducts = () => {
    return cartItems.filter(item => {
      const key = `${item.id}-${item.colorId}-${item.size}`;
      return (stockLimits[key] === 0 || stockLimits[key] === undefined);
    });
  };
  
  const hasUnavailableItems = () => getUnavailableProducts().length > 0;
  
  // Handle payment method change
  const handlePaymentMethodChange = (event) => {
    setPaymentMethod(event.target.value);
  };

  // Handle checkout process
  const handleCheckout = async () => {
    // Check if governorate is selected (still required even with free shipping)
    if (!selectedGovernorate) {
      setNotification({
        open: true,
        message: 'يرجى اختيار المحافظة أولاً',
        severity: 'warning'
      });
      return;
    }

    // Show a subtle loading indicator
    setRefreshingStock(true);
    
    try {
      // Force a complete refresh of stock data by directly querying each item
      // Create a copy to avoid issues if items are modified during the operation
      const currentItems = [...cartItems];
      
      // Query directly from database for most up-to-date stock information
      const stockPromises = currentItems.map(async (item) => {
        try {
          const { data, error } = await supabase
            .from('product_color_sizes')
            .select('stock')
            .eq('product_color_id', item.colorId)
            .eq('size', item.size)
            .single();
            
          if (error) {
            console.error('Supabase error during checkout:', error);
            return { 
              key: `${item.id}-${item.colorId}-${item.size}`, 
              stock: 0,
              item: item 
            };
          }
          
          return { 
            key: `${item.id}-${item.colorId}-${item.size}`, 
            stock: data?.stock || 0,
            item: item
          };
        } catch (err) {
          console.error('Error fetching stock for item during checkout:', err);
          return { 
            key: `${item.id}-${item.colorId}-${item.size}`, 
            stock: 0,
            item: item
          };
        }
      });

      // Wait for all stock checks to complete
      const stockResults = await Promise.all(stockPromises);
      
      // Update the stock limits in state to ensure UI consistency
      const newStockLimits = {...stockLimits};
      stockResults.forEach(({ key, stock }) => {
        newStockLimits[key] = stock;
      });
      setStockLimits(newStockLimits);
      
      // Check for unavailable items and items with insufficient stock
      const unavailableItems = [];
      const insufficientStockItems = [];
      
      stockResults.forEach(result => {
        const { stock, item } = result;
        
        if (stock === 0) {
          unavailableItems.push(item);
        } else if (stock < item.quantity) {
          insufficientStockItems.push({
            ...item,
            availableStock: stock
          });
        }
      });
      
      // Handle unavailable items first (stock = 0)
      if (unavailableItems.length > 0) {
        setNotification({
          open: true,
          message: 'هناك منتجات غير متوفرة في المخزون',
          severity: 'error'
        });
        return;
      }
      
      // Handle items with insufficient stock (stock < quantity)
      if (insufficientStockItems.length > 0) {
        // Automatically adjust quantities for items with insufficient stock
        const adjustments = insufficientStockItems.map(item => {
          return `${item.name_ar} (${item.quantity} → ${item.availableStock})`;
        }).join(', ');
        
        setNotification({
          open: true,
          message: `تم تعديل الكميات بسبب نقص المخزون: ${adjustments}`,
          severity: 'warning'
        });
        
        // Update quantities to match available stock
        insufficientStockItems.forEach(item => {
          updateQuantity(item.id, item.colorId, item.size, item.availableStock);
        });
        
        // Don't proceed with checkout yet - let user see the adjustments
        return;
      }
      
      // All items available with sufficient stock, proceed to checkout
      // Pass shipping information to checkout
      const selectedShippingInfo = shippingPrices.find(price => price.id === selectedGovernorate);
      navigate('/checkout', {
        state: {
          shippingInfo: selectedShippingInfo,
          totalAmount: finalTotal,
          hasAllFreeShipping: hasAllFreeShipping,
          actualShippingCost: finalShippingPrice
        }
      });
    } catch (err) {
      console.error('Error during checkout process:', err);
      setNotification({
        open: true,
        message: 'حدث خطأ أثناء التحقق من المخزون، يرجى المحاولة مرة أخرى',
        severity: 'error'
      });
    } finally {
      setRefreshingStock(false);
    }
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#fafafa', direction: 'rtl' }}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <Typography sx={{ fontWeight: 'bold' }}>جاري التحميل...</Typography>
        </Box>
      ) : cartItems.length === 0 ? (
        <Box textAlign="center" sx={{ mt: 6 }}>
          <Typography variant="h6">سلة التسوق فارغة</Typography>
          <Button component={Link} to="/" variant="contained" sx={{ mt: 2 }}>
            ابدأ بالتسوق
          </Button>
        </Box>
      ) : (
        <Grid container spacing={4}>
          {/* Items List */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 1 }}>
              <CartTable 
                cartItems={cartItems}
                stockLimits={stockLimits}
                updateQuantity={updateQuantity}
                removeFromCart={removeFromCart}
                handleDropdownOpen={handleDropdownOpen}
                refreshingStock={refreshingStock}
                productShippingInfo={productShippingInfo}
              />
            </Paper>
          </Grid>

          {/* Summary */}
          <Grid item xs={12} md={4}>
            <CartSummary 
              cartCount={cartCount}
              totalAmount={totalAmount}
              shippingPrice={finalShippingPrice}
              originalShippingPrice={shippingPrice}
              finalTotal={finalTotal}
              shippingPrices={shippingPrices}
              selectedGovernorate={selectedGovernorate}
              handleGovernorateChange={handleGovernorateChange}
              loadingShipping={loadingShipping}
              paymentMethod={paymentMethod}
              handlePaymentMethodChange={handlePaymentMethodChange}
              hasUnavailableItems={hasUnavailableItems}
              refreshingStock={refreshingStock}
              cartItems={cartItems}
              handleCheckout={handleCheckout}
              clearCart={clearCart}
              hasAllFreeShipping={hasAllFreeShipping}
            />
          </Grid>
        </Grid>
      )}
      
      {/* Notification system for stock alerts */}
      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity} 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Cart;
